import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoMailOutline } from 'react-icons/io5';
import { getNylasStatus, startNylasConnect, disconnectNylas, setDefaultNylasAccount } from '../api';
import { startGmailConnect, finishGmailConnect, disconnectEmailAccount } from './atlas/atlasApi';

const PROVIDER_LABEL = { google: 'Gmail', microsoft: 'Outlook', imap: 'IMAP' };

// After Nylas hosted auth (Outlook, IMAP), realtorch-backend redirects back with ?nylas=<result>.
// Gmail signs in through Composio in a new tab; when popups are blocked the whole page
// goes and comes back to Settings → Connectors with ?gmail=finish.
const CONNECT_RESULT = {
  connected: { tone: 'ok', text: 'Email account connected.' },
  limit: { tone: 'error', text: 'You already have the maximum number of email accounts connected. Disconnect one first.' },
  error: { tone: 'error', text: 'The email connection did not complete. Please try again.' },
};

// Older backends return a single {connected, email, provider}; newer ones a list.
function accountsOf(status) {
  if (Array.isArray(status?.accounts)) return status.accounts;
  return status?.connected ? [{ id: null, email: status.email, provider: status.provider, is_default: true }] : [];
}

const GMAIL_POLL_MS = 1000;
const GMAIL_POLL_LIMIT_MS = 10 * 60 * 1000;

// Reads and removes ?<name>=… from the address bar.
function takeParam(name) {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(name);
    if (!value) return null;
    params.delete(name);
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    return value;
  } catch {
    return null;
  }
}

function readConnectResult() {
  return CONNECT_RESULT[takeParam('nylas')] || null;
}

function gmailNotice(out) {
  if (out?.result === 'connected') return { tone: 'ok', text: `${out.email || 'Gmail'} connected.` };
  if (out?.result === 'limit' || out?.result === 'error') return CONNECT_RESULT[out.result];
  return null;
}

// Connect, disconnect, and choose the default mailbox. A user can connect up to
// max_accounts (default 2); Atlas searches all of them and suggests which one an
// email goes from, which the user confirms on the approval card.
export default function EmailConnectionCard() {
  const [status, setStatus] = useState(null); // {connected, accounts: [...], max_accounts}
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(() => readConnectResult());
  const [backFromGmail] = useState(() => new URLSearchParams(window.location.search).get('gmail') === 'finish');
  const gmailFinished = useRef(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setStatus(await getNylasStatus());
      setError(null);
    } catch (e) {
      setError('Could not load email connection status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Record the Gmail the user just signed in to, then show the mailbox list again.
  const finishGmail = useCallback(async () => {
    try {
      setWorking(true);
      setError(null);
      setNotice(gmailNotice(await finishGmailConnect()));
    } catch (e) {
      setError('Could not finish connecting Gmail. Please try again.');
    } finally {
      setWorking(false);
      refresh();
    }
  }, [refresh]);

  useEffect(() => {
    if (!backFromGmail || gmailFinished.current) return;
    gmailFinished.current = true;
    takeParam('gmail');
    finishGmail();
  }, [backFromGmail, finishGmail]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const connectGmail = useCallback(async () => {
    setWorking(true);
    setError(null);
    setNotice(null);
    // Open the tab inside the click so popup blockers allow it.
    const popup = window.open('', '_blank');
    const origin = window.location.origin;
    try {
      const { redirect_url: url } = await startGmailConnect(
        popup ? `${origin}/connected-app.html` : `${origin}/?settings=connectors&gmail=finish`,
      );
      if (!popup) {
        window.location.href = url;
        return;
      }
      popup.location.href = url;
      setNotice({ tone: 'ok', text: 'Finish signing in to Google in the new tab.' });
      setWorking(false);
      if (pollRef.current) clearInterval(pollRef.current);
      const started = Date.now();
      pollRef.current = setInterval(() => {
        if (popup.closed || Date.now() - started > GMAIL_POLL_LIMIT_MS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          finishGmail();
        }
      }, GMAIL_POLL_MS);
    } catch (e) {
      if (popup) popup.close();
      setError(e?.message || 'Could not start the Gmail sign-in.');
      setWorking(false);
    }
  }, [finishGmail]);

  const connect = useCallback(async (provider) => {
    if (provider === 'google') {
      connectGmail();
      return;
    }
    try {
      setWorking(true);
      setError(null);
      setNotice(null);
      const { auth_url } = await startNylasConnect(provider);
      if (auth_url) {
        window.location.href = auth_url; // hand off to Nylas hosted auth
      } else {
        setError('Email connection is not available right now.');
        setWorking(false);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not start the email connection.');
      setWorking(false);
    }
  }, [connectGmail]);

  const act = useCallback(async (fn, failure) => {
    try {
      setWorking(true);
      setError(null);
      setNotice(null);
      const next = await fn();
      if (next && (Array.isArray(next.accounts) || 'connected' in next)) setStatus(next);
      else await refresh();
    } catch (e) {
      setError(failure);
    } finally {
      setWorking(false);
    }
  }, [refresh]);

  // A Gmail connected through Composio is removed by Atlas; any other mailbox by Nylas.
  const disconnect = (account) => act(async () => {
    if (account.id != null && account.provider === 'google') {
      const out = await disconnectEmailAccount(account.id);
      if (out?.handled) return null;
    }
    return disconnectNylas(account.id ?? undefined);
  }, 'Could not disconnect.');

  const accounts = accountsOf(status);
  const maxAccounts = Number(status?.max_accounts || 1);
  const canAdd = accounts.length < maxAccounts;
  const several = accounts.length > 1;

  const wrap = { border: '1px solid #e5e1d8', borderRadius: 10, padding: 16, marginTop: 16 };
  const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 };
  const btn = { padding: '8px 14px', minHeight: 40, borderRadius: 8, border: '1px solid #d6d1c4', background: '#fff', cursor: 'pointer', fontSize: 13 };
  const small = { ...btn, padding: '8px 12px', fontSize: 13, minHeight: 36 };
  const primary = { ...btn, background: '#1a1a1a', color: '#fff', border: 'none' };
  const badge = { fontSize: 11, fontWeight: 600, color: '#4338ca', background: '#eef2ff', borderRadius: 999, padding: '2px 8px' };

  return (
    <div style={wrap}>
      <div style={row}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IoMailOutline size={18} />
          <span style={{ fontWeight: 600 }}>Email</span>
        </div>
      </div>

      {notice && (
        <div style={{ marginTop: 10, fontSize: 12, color: notice.tone === 'ok' ? '#2f7a4f' : '#b4423a' }}>{notice.text}</div>
      )}

      {loading ? (
        <div style={{ marginTop: 10, fontSize: 13, color: '#888' }}>Loading…</div>
      ) : (
        <>
          {accounts.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {accounts.map((account) => (
                <div key={account.id ?? account.email} style={{ ...row, flexWrap: 'wrap', fontSize: 13, color: '#444' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <strong style={{ overflowWrap: 'anywhere' }}>{account.email || 'Connected mailbox'}</strong>
                    {account.provider && <span style={{ color: '#888' }}>{PROVIDER_LABEL[account.provider] || account.provider}</span>}
                    {several && account.is_default && <span style={badge}>Default</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {account.provider === 'google' && (
                      <button type="button" style={small} disabled={working}
                              aria-label={`Reconnect ${account.email || 'this mailbox'}`}
                              onClick={connectGmail}>
                        Reconnect
                      </button>
                    )}
                    {several && !account.is_default && account.id != null && (
                      <button type="button" style={small} disabled={working}
                              onClick={() => act(() => setDefaultNylasAccount(account.id), 'Could not change the default account.')}>
                        Make default
                      </button>
                    )}
                    <button type="button" style={small} disabled={working}
                            aria-label={`Disconnect ${account.email || 'this mailbox'}`}
                            onClick={() => disconnect(account)}>
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 12, color: '#888' }}>
                {several
                  ? 'Atlas searches all your connected mailboxes and suggests which address an email goes from; you confirm it before anything is sent.'
                  : 'Atlas can read and draft email in this mailbox. It never sends without you.'}
              </div>
            </div>
          )}

          {canAdd ? (
            <div style={{ marginTop: accounts.length ? 14 : 10 }}>
              <div style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>
                {accounts.length
                  ? `Add another email account (up to ${maxAccounts}), for example work and personal.`
                  : 'Connect your inbox so Atlas can read, draft, and send emails for you.'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" style={primary} disabled={working} onClick={() => connect('google')}>
                  {accounts.length ? 'Add Gmail' : 'Connect Gmail'}
                </button>
                <button type="button" style={primary} disabled={working} onClick={() => connect('microsoft')}>
                  {accounts.length ? 'Add Outlook' : 'Connect Outlook'}
                </button>
                <button type="button" style={btn} disabled={working} onClick={() => connect('imap')}>
                  Other (IMAP)
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                Gmail and Outlook use secure sign-in{accounts.length ? '; sign in to the account you want to add' : ''}. For other providers
                (iCloud, Yahoo, Fastmail, Zoho, etc.), use <strong>Other (IMAP)</strong> — most require an{' '}
                <strong>app password</strong> from your email provider, not your normal password.
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
              You can connect up to {maxAccounts} email accounts. Disconnect one to add another.
            </div>
          )}
        </>
      )}

      {error && <div style={{ marginTop: 10, fontSize: 12, color: '#b4423a' }}>{error}</div>}
    </div>
  );
}
